<template>
  <cso-dialog ref="dialog"
    custom-class="birthday-modal">
    <div class="missing-data-modal"
        v-if="isBirthdayMissing || isPhoneMissing || isDeliveryTypeNotMail"
        :class="{'has-overlay': birthdateSending}">
      <h3>{{ $t('missingData.headline') }}</h3>
      <p v-if="isBirthdayMissing">
        {{ $t('missingData.birthdayMissingText') }}
        <strong v-if="birthdateName" class="birthdate-name">{{ birthdateName }}</strong>
      </p>
      <p v-if="isPhoneMissing">{{ $t('missingData.phoneMissingText') }}</p>
      <p v-if="isDeliveryTypeNotMail">{{ $t('contractAndDelivery.offlineCommunicationHint') }}</p>
      <cso-button
          @click="showBirthdateField()"
          :label="$t('missingData.birthdayMissingButton')"
          v-if="isBirthdayMissing && !isBirthdayFieldShown" />
      <cso-button
          @click="changePhoneNumber()"
          :label="$t('missingData.phoneMissingButton')"
          v-if="isPhoneMissing" />
      <cso-button
          @click="changeDeliveryType()"
          :label="$t('contractAndDelivery.buttons.offlineCommunication')"
          v-if="isDeliveryTypeNotMail" />
      <cso-grid-row v-if="isBirthdayFieldShown">
        <cso-grid-column xs="12"
                        s="8">
          <cso-datepicker name="birthdate"
                          :label="$t('missingData.birthdate')"
                          :validation="v$.personBirthdate"
                          validation-name="missingData.validations.birthdate"
                          v-model="personBirthdate"
                          direction="descending"
                          :is-nullable="true" />
          <div v-if="birthdateError" class="error">
            {{ birthdateError }}
          </div>
        </cso-grid-column>
        <cso-grid-column xs="12"
                        s="4">
          <cso-button
                      class="save-birthdate-button"
                      :label="$t('general.save')"
                      @click="saveBirthdate()" />
        </cso-grid-column>
      </cso-grid-row>
    </div>
  </cso-dialog>
</template>

<script lang="ts">
import { useVuelidate } from '@vuelidate/core'
import { required } from '@vuelidate/validators'

import store from '@/store'
import di from '@/api/digital-interface'
import * as DI from '@/api/digital-interface/client'

import CsoDialog from '@/components/core/composition/CsoDialog.vue'
import CsoDatepicker from '@/components/core/form/CsoDatepicker.vue'
import LoggingService from '@/common/LoggingService'

interface Dialog {
  open (): void
  close (): void
}

export default {
  name: 'CsoMissingDataCheck',

  components: {
    CsoDialog,
    CsoDatepicker
  },

  data () {
    return {
      isBirthdayMissing: false,
      isPhoneMissing: false,
      isDeliveryTypeNotMail: false,
      isBirthdayFieldShown: false,
      birthdateError: '',
      birthdateSending: false,
      birthdateName: '',
      personBirthdate: null
    }
  },

  validations () {
    return {
      personBirthdate: {
        required
      }
    }
  },

  methods: {
    checkMissingBirthday () {
      LoggingService.log(LoggingService.eventTypes.userEvent, 'Missing birthday check')
      if (!!store.GP?.Geburtsdatum) {
        return
      }
      this.birthdateName =
          ((store.GP?.Anrede?.Anzeigetext ? store.GP?.Anrede?.Anzeigetext + ' ' : '') +
          (store.GP?.Name2_Vorname ? store.GP?.Name2_Vorname + ' ' : '') +
          store.GP?.Name1_Nachname) || ''
      this.isBirthdayMissing = true
      this.openDialog()
    },

    checkMissingPhone () {
      LoggingService.log(LoggingService.eventTypes.userEvent, 'Missing phone check')
      if (!!store.account?.Mobilnummer) {
        return
      }
      this.isPhoneMissing = true
      this.openDialog()
    },

    checkDeliveryType () {
      LoggingService.log(LoggingService.eventTypes.userEvent, 'Delivery type check')
      if (!store.GP?.Vertraege.some(vk => vk.DokumentenVersandArt != null && vk.DokumentenVersandArt !== 'MAIL')) {
        return
      }
      this.isDeliveryTypeNotMail = true
      this.openDialog()
    },

    openDialog () {
      LoggingService.log(LoggingService.eventTypes.userEvent, 'Open missing data dialog')
      const dialog = this.$refs.dialog as Dialog
      dialog.open()
    },

    closeDialog () {
      LoggingService.log(LoggingService.eventTypes.userEvent, 'Close missing data dialog')
      const dialog = this.$refs.dialog as Dialog
      dialog.close()
    },

    changePhoneNumber () {
      LoggingService.log(LoggingService.eventTypes.userEvent, 'Change phone number button clicked')
      this.$router.push({ name: 'personalData', query: { phone: 'true' } })
      this.closeDialog()
    },

    changeDeliveryType () {
      LoggingService.log(LoggingService.eventTypes.userEvent, 'Change delivery type button clicked')
      this.$router.push({ name: 'communication' })
      this.closeDialog()
    },

    showBirthdateField () {
      this.isBirthdayFieldShown = true
    },

    async saveBirthdate () {
      LoggingService.log(LoggingService.eventTypes.userEvent, 'Save birthday button clicked')
      if (this.birthdateSending === true || !await this.validate()) {
        return
      }
      this.birthdateError = ''
      this.birthdateSending = true
      try {
        await this.updateBirthdate()
        this.birthdateSending = false
        this.closeDialog()
      } catch (error) {
        this.handleError(error)
        this.birthdateSending = false
      }
    },

    async validate () {
      this.v$.$touch()
      if (this.v$.$invalid) {
        return false
      }
      if (!await this.validatePersonBirthdate()) {
        return false
      }
      return true
    },

    async validatePersonBirthdate () {
      this.birthdateError = ''
      if (this.personBirthdate) {
        const check = await di.v14.checkBirthdate(this.personBirthdate)
        if (check.Error) {
          this.birthdateError = check.Meldungen
        }
      }
      return this.birthdateError === ''
    },

    handleError (error: any) {
      if (error instanceof DI.ErrorMessage || error instanceof DI.ErrorResponse) {
        this.birthdateError = error.text
      } else {
        throw error
      }
    },

    async updateBirthdate () {
      await di.v14.updateBirthdate(store.GP!.GP.ReferenzID, this.personBirthdate!)
      await store.clearVertragsliste()
      await store.fetchVertragsliste()
    },
  },

  setup () {
    return {
      v$: useVuelidate()
    }
  },

  async mounted () {
    if (store.GP?.GP?.Typ === 'PERSON') {
      this.checkMissingBirthday()
      if (!this.isBirthdayMissing) {
        this.checkMissingPhone()
        if (!this.isPhoneMissing) {
          this.checkDeliveryType()
        }
      }
    }
    store.clearAfterLoginHook()
  }
}
</script>

<style lang="scss">

.missing-data-modal {
  font-size: 18px;
  line-height: 125%;
  font-family: $font-family-regular;

  @include xs {
    padding: 0 12px;
  }

  h3 {
    font-family: $font-family-bold;
    font-size: 32px;
    margin-bottom: 22px;
    line-height: 125%;

    @include md {
      font-size: 24px;
      margin-bottom: 12px;
    }
  }


  p {
    font-size: 18px;
    line-height: 125%;
    margin-bottom: 40px;
  }

  .error {
    margin: 16px 0 0;

    @include error-message();
  }

  .save-birthdate-button {
    margin-top: 20px;
  }
}

.birthday-modal {
  .sweet-modal {
    overflow-y: visible;

    @include md {
      width: 100vw !important;
    }
  }

  .birthdate-name {
    display: block;
    padding-top: 20px;
  }
}
</style>
